/**
 * Exports, as plain CSV or as a colour-coded spreadsheet.
 *
 * Supervisor-only, matching the server: a recruiter can read their own rows on
 * screen but not pull them out as a file. Showing them a button that can only
 * 403 would be worse than not showing it.
 *
 * Only the filters the server actually honours for each export are offered —
 * candidates belong to the company rather than to a client, so no client
 * filter appears for them, and asking for one is a 422 rather than a silently
 * ignored parameter.
 *
 * Every download is audited server-side. The note on the page says so, because
 * people should know that before they pull a file of personal data, not after.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Download, FileSpreadsheet, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import {
  downloadExport,
  exportErrorMessage,
  type ExportFormat,
} from '../../lib/exportDownload';
import { isSupervisor, useAuth } from '../../store/auth';

type Kind = 'calls' | 'candidates' | 'submissions' | 'placements' | 'invoices';

interface ExportKind {
  key: Kind;
  label: string;
  blurb: string;
  /** Whether the server accepts these filters for this export. */
  client: boolean;
  statuses: readonly string[];
  dateLabel: string;
  /** Whether the spreadsheet colours rows by status for this export. */
  coloured?: boolean;
}

const KINDS: readonly ExportKind[] = [
  {
    key: 'calls',
    label: 'Calls',
    blurb: 'Every dial, its outcome, and the recruiter’s own words.',
    client: true,
    statuses: [
      'interested', 'shortlisted', 'call_back', 'hold', 'not_interested',
      'not_picking', 'already_working', 'not_looking', 'busy',
      'location_issue', 'salary_issue', 'invalid_number', 'switched_off',
      'asked_for_referral', 'language_barrier', 'not_suitable',
      'missing_requirement', 'wrong_profile', 'info_sent',
      'awaiting_confirmation', 'notice_period', 'other',
    ],
    dateLabel: 'Call',
    coloured: true,
  },
  {
    key: 'candidates',
    label: 'Candidates',
    blurb: 'The talent pool, with contact details and salary expectations.',
    client: false,
    statuses: [],
    dateLabel: 'Added',
  },
  {
    key: 'submissions',
    label: 'Submissions',
    blurb: 'Who was put forward, to which client, and where it got to.',
    client: true,
    statuses: [
      'pending', 'shortlisted', 'selected', 'rejected',
      'hold', 'followup', 'yet_to_join', 'working',
    ],
    dateLabel: 'Submitted',
    coloured: true,
  },
  {
    key: 'placements',
    label: 'Placements',
    blurb: 'Everyone who joined, with CTC, billing and whether they were onboarded.',
    client: true,
    statuses: ['yet_to_join', 'working', 'drop', 'not_joined', 'rejected', 'hold'],
    dateLabel: 'Joining',
    coloured: true,
  },
  {
    key: 'invoices',
    label: 'Invoices',
    blurb: 'Raised, received and outstanding — the reconciliation view.',
    client: true,
    statuses: ['draft', 'raised', 'partially_paid', 'paid', 'overdue', 'cancelled'],
    dateLabel: 'Invoice',
  },
];

function ExportCard({ kind }: { kind: ExportKind }) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('');

  const download = useMutation({
    mutationFn: async (format: ExportFormat) =>
      downloadExport({
        kind: kind.key,
        format,
        params: { dateFrom, dateTo, status },
      }),
    onSuccess: (name) => toast.success(`Saved ${name}`),
    onError: (error) => {
      void exportErrorMessage(error).then((message) => toast.error(message));
    },
  });

  return (
    <section className="card space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{kind.label}</h3>
        <p className="mt-1 text-xs text-gray-500">{kind.blurb}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-xs text-gray-600">
          {kind.dateLabel} from
          <input
            className="input mt-1"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>
        <label className="text-xs text-gray-600">
          {kind.dateLabel} to
          <input
            className="input mt-1"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </label>
        {kind.statuses.length > 0 && (
          <label className="text-xs text-gray-600">
            Status
            <select
              className="input mt-1"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Any</option>
              {kind.statuses.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {kind.coloured && (
          <span className="mr-auto text-[11px] text-gray-400">
            The spreadsheet colours each row by status.
          </span>
        )}
        <button
          type="button"
          className="btn-ghost text-xs"
          disabled={download.isPending}
          onClick={() => download.mutate('csv')}
        >
          <Download className="h-4 w-4" />
          CSV
        </button>
        <button
          type="button"
          className="btn-secondary"
          disabled={download.isPending}
          onClick={() => download.mutate('xlsx')}
        >
          {download.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="h-4 w-4" />
          )}
          {kind.coloured ? 'Colour-coded Excel' : 'Excel'}
        </button>
      </div>
    </section>
  );
}

export default function ExportsPage() {
  const user = useAuth((s) => s.user);

  if (!isSupervisor(user)) {
    return (
      <div className="card flex items-center gap-3 text-sm text-gray-600">
        <ShieldAlert className="h-4 w-4 text-gray-400" />
        Exports are limited to managers and administrators.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="card">
        <h2 className="text-sm font-semibold text-gray-900">Exports</h2>
        <p className="mt-1 max-w-2xl text-xs text-gray-500">
          Download what is in the system. Both formats open in Excel or
          Sheets, with dates and amounts written so a spreadsheet cannot
          re-interpret them. The Excel version colours each row by status,
          the way the old tracker sheet did — the status is still spelled out
          in its own column, so the colour is a shortcut rather than the only
          way to read it.
        </p>
        <p className="mt-2 max-w-2xl text-xs text-gray-500">
          These files contain personal data. Every download is recorded —
          what was exported, how many rows, and by whom.
        </p>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        {KINDS.map((kind) => (
          <ExportCard key={kind.key} kind={kind} />
        ))}
      </div>
    </div>
  );
}
